import React, { useRef, useState } from 'react';
import Icon from './ui/Icon';
import Reveal, { RevealGroup } from './ui/Reveal';
import SectionHeading from './ui/SectionHeading';

import { pillars } from '../data/content';

/**
 * "Why TEEEM?" — the four actions the name stands for.
 *
 * The grid doubles as a tab list: selecting an action reveals the brief's full
 * treatment of it below, which keeps the whole of that content on the page
 * without turning the section into four columns of dense text.
 */
export default function Pillars() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);

  const onKeyDown = (event) => {
    const offset = { ArrowRight: 1, ArrowLeft: -1, Home: -active, End: pillars.length - 1 - active }[event.key];
    if (offset === undefined) return;
    event.preventDefault();
    const next = (active + offset + pillars.length) % pillars.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const detail = pillars[active].detail;

  return (
    <section id="about" className="bg-cream py-24 sm:py-32">
      <div className="shell">
        <SectionHeading
          index="02"
          label="Why TEEEM"
          title={
            <>
              Why <span className="border-b-2 border-gold pb-1.5">TEEEM</span>?
            </>
          }
          lead="TEEEM stands for four actions that define how we approach ministry and change lives."
        />

        <Reveal
          as="p"
          variant="fade"
          delay={400}
          className="mt-8 text-center text-[0.7rem] uppercase tracking-[0.3em] text-ink/60"
        >
          Select an action to read more
        </Reveal>

        <RevealGroup
          role="tablist"
          aria-label="The four actions"
          onKeyDown={onKeyDown}
          stagger={110}
          delay={120}
          className="mt-12 grid gap-y-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-y-0"
        >
          {pillars.map((pillar, i) => {
            const selected = i === active;
            return (
              <div key={pillar.id} className="lg:border-l lg:border-ink/10 lg:first:border-l-0">
                <button
                  type="button"
                  role="tab"
                  id={`pillar-tab-${pillar.id}`}
                  aria-selected={selected}
                  aria-controls="pillar-panel"
                  tabIndex={selected ? 0 : -1}
                  ref={(node) => {
                    tabRefs.current[i] = node;
                  }}
                  onClick={() => setActive(i)}
                  className="group flex h-full w-full cursor-pointer flex-col items-center px-6 text-center"
                >
                  <img
                    src={pillar.icon}
                    alt=""
                    width="256"
                    height="256"
                    loading="lazy"
                    className={`h-[88px] w-[88px] transition-all duration-500 ease-out-expo ${
                      selected
                        ? 'scale-105 drop-shadow-[0_10px_24px_rgba(15,15,16,0.22)]'
                        : 'opacity-70 group-hover:scale-105 group-hover:opacity-100'
                    }`}
                  />
                  <h3
                    className={`mt-6 border-b-2 pb-1.5 font-display text-lg font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${
                      selected
                        ? 'border-gold text-ink'
                        : 'border-transparent text-ink/65 group-hover:border-gold/40 group-hover:text-ink'
                    }`}
                  >
                    {pillar.title}
                  </h3>
                  <p className="mt-4 max-w-xs text-sm leading-relaxed text-ink/65">{pillar.summary}</p>
                </button>
              </div>
            );
          })}
        </RevealGroup>

        <div
          role="tabpanel"
          id="pillar-panel"
          aria-labelledby={`pillar-tab-${pillars[active].id}`}
          tabIndex={-1}
          className="mt-14 border-t border-ink/10 pt-14"
        >
          {/* Keyed so the panel replays its entrance each time an action changes. */}
          <div key={pillars[active].id} className="grid gap-12 lg:grid-cols-2 lg:gap-20">
            <div style={{ animation: 'fade-up 0.7s cubic-bezier(0.16, 1, 0.3, 1) both' }}>
              <p className="font-display text-xl uppercase leading-snug tracking-[0.06em] text-gold-700 sm:text-[1.6rem]">
                {detail.lead}
              </p>
              <p className="mt-6 text-[0.98rem] leading-relaxed text-ink/75">{detail.body}</p>
            </div>

            <div style={{ animation: 'fade-up 0.7s cubic-bezier(0.16, 1, 0.3, 1) both', animationDelay: '120ms' }}>
              <ul className="grid gap-x-10 gap-y-3 sm:grid-cols-2">
                {detail.items.map((item) => (
                  <li key={item} className="flex gap-3 text-[0.98rem] leading-relaxed text-ink/80">
                    <Icon name="check" className="mt-1 h-4 w-4 shrink-0 text-gold" strokeWidth={2.4} />
                    {item}
                  </li>
                ))}
              </ul>

              {detail.emphasis && (
                <div className="mt-8 border-l-2 border-gold pl-5">
                  {detail.emphasis.map((line) => (
                    <p key={line} className="text-[0.98rem] font-medium leading-relaxed text-ink">
                      {line}
                    </p>
                  ))}
                </div>
              )}

              <p className="mt-8 text-[0.98rem] leading-relaxed text-ink/75">{detail.close}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
